// src/oura/merchant/ReportLine.tsx
//
// One report in the merchant's Documents list (redesign): its icon, its title
// with a blue dot while not opened, the date and pages, a Summary toggle that
// opens what the report says in the row, and View, which opens the report
// drawer (the PDF viewer or the in-app snapshot).

import { useState } from "react";
import { ChevronDownIcon, FileCheck2Icon, FileTextIcon } from "lucide-react";
import { isInAppSnapshot, type ClientReportLink } from "@service/aeo/clientReview";
import { ArrowLink, FOCUS_RING, IconSquare, ListRow, ROW_OWN, TYPE } from "@oura/ui";
import { cn } from "../lib/cn";
import { ReportMeta } from "./drawers/ReportDrawer";

export function ReportLine({ report: r, isNew, onOpen }: { report: ClientReportLink; isNew: boolean; onOpen: () => void }) {
    // What a report says, readable without opening the PDF (16 Oct 2026). Closed to start.
    const [described, setDescribed] = useState(false);
    const sections = r.sections?.filter((x) => x.trim()) ?? [];
    const hasText = Boolean(r.summary?.trim()) || sections.length > 0;
    const openable = Boolean(r.url) || isInAppSnapshot(r);
    const panelId = `report-summary-${r.id}`;
    return (
        <div>
            <ListRow
                icon={<IconSquare tone={openable ? "blue" : "gray"} icon={r.kind === "doc_approved" ? FileCheck2Icon : FileTextIcon} />}
                title={
                    // Not opened yet: a blue dot before the title, in place of a "New" chip.
                    isNew ? (
                        <span className="inline-flex items-center gap-2">
                            <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-blue-600" />
                            {r.title}
                            <span className="sr-only"> (not opened yet)</span>
                        </span>
                    ) : (
                        r.title
                    )
                }
                sub={
                    <>
                        <ReportMeta report={r} />
                        {hasText && (
                            <button
                                type="button"
                                aria-expanded={described}
                                aria-controls={panelId}
                                onClick={() => setDescribed((was) => !was)}
                                className={cn(TYPE.tc, ROW_OWN, "mt-1 flex items-center gap-1 rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}
                            >
                                {described ? "Hide summary" : "Summary"}
                                <ChevronDownIcon aria-hidden size={14} className={cn("transition-transform duration-200 motion-reduce:transition-none", described && "rotate-180")} />
                            </button>
                        )}
                    </>
                }
                muted={!openable}
                // The whole row opens the report; Summary keeps its own click.
                stretch={openable || hasText}
                right={
                    // No file and nothing to read about it: no View that opens onto "no file yet".
                    openable || hasText ? (
                        <ArrowLink stretch onClick={onOpen}>View</ArrowLink>
                    ) : (
                        <span className={cn(TYPE.tc, "text-gray-400")}>Not uploaded yet</span>
                    )
                }
                className="py-3"
            />
            {/* The same grey panel a To do row opens into. */}
            {hasText && described && (
                <div id={panelId} className="mb-3 ml-12 space-y-3 rounded-xl bg-gray-50 p-4">
                    {r.summary?.trim() && <p className={cn(TYPE.tb, "m-0 text-gray-700")}>{r.summary}</p>}
                    {sections.length > 0 && (
                        <div>
                            <p className={cn(TYPE.lab, "m-0")}>Inside</p>
                            <ol className="list-plain m-0 mt-1 p-0">
                                {sections.map((section, i) => (
                                    <li key={section} className={cn(TYPE.tb, "flex min-h-8 items-center gap-3 text-gray-700")}>
                                        <span className={cn(TYPE.tm, "w-5 tabular-nums text-gray-400")}>{i + 1}</span>
                                        {section}
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
